import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Chip,
  CircularProgress,
  Container,
  Link as MuiLink,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { FormRecord, fetchFormsForDomain } from '../utils/formsApi';
import { apiDomain } from '../utils/apiClient';

const getFormRendererUrl = (uuid: string) => `${window.location.origin}/?formUUID=${encodeURIComponent(uuid)}`;

const FormsListPage: React.FC = () => {
  const [forms, setForms] = useState<FormRecord[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    fetchFormsForDomain()
      .then((availableForms) => {
        if (isMounted) {
          setForms(availableForms);
        }
      })
      .catch((requestError) => {
        console.error('Failed to fetch forms:', requestError);
        if (isMounted) {
          setError('Failed to load the available forms.');
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4, textAlign: 'left' }}>
      <Typography component={Link} to="/" color="primary" sx={{ textDecoration: 'none' }}>
        ← Form Renderer
      </Typography>
      <Typography variant="h4" sx={{ mt: 2, mb: 1 }}>
        Available Forms
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Forms available for domain {apiDomain}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {isLoading ? (
        <CircularProgress aria-label="Loading forms" />
      ) : (
        <Paper>
          <TableContainer>
            <Table aria-label="Available forms">
              <TableHead>
                <TableRow>
                  <TableCell>Key</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>UUID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {forms.map((form) => (
                  <TableRow key={form.uuid} hover>
                    <TableCell><MuiLink component={Link} to={`/forms/${encodeURIComponent(form.uuid)}`}>{form.key}</MuiLink></TableCell>
                    <TableCell><Chip label={form.status} size="small" color={form.status === 'ACTIVE' ? 'success' : 'default'} /></TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                      <MuiLink href={getFormRendererUrl(form.uuid)} target="_blank" rel="noopener noreferrer">
                        {form.uuid}
                      </MuiLink>
                    </TableCell>
                    <TableCell>{form.name}</TableCell>
                    <TableCell>{form.description || '—'}</TableCell>
                  </TableRow>
                ))}
                {forms.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">No forms are available for this domain.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Container>
  );
};

export default FormsListPage;
